// Simulador de perfil pra ADMIN -- deixa quem tem ADMIN de verdade "ver
// como" outro papel (Secretaria, Acolhimento) sem trocar de conta, so pra
// conferir o que cada papel enxerga no app. Guardado apenas no
// localStorage de quem ativou (nunca no backend/sessao), entao nao afeta
// nenhuma outra pessoa, aba ou dispositivo. So muda o que aparece na tela
// -- o backend continua autorizando pela role real da sessao, entao
// nenhuma acao de fato fica mais nem menos restrita so por causa do modo
// estar ligado (ele simplesmente esconde/mostra UI como se fosse o papel
// escolhido).
const STORAGE_KEY = "mosaico_admin_view_as_role";
const CHANGE_EVENT = "mosaico:viewAsRoleChange";

// Mantido em sincronia manual com ROLE_OPTIONS em AdminUsuarios.jsx (papeis
// reais no banco) -- toda vez que um papel novo for criado la', precisa
// entrar aqui tambem pra dar pra simular (RECEPCAO e FINANCEIRO ja' foram
// esquecidos assim antes). "MEMBER" (o papel real "Membro" no banco, so'
// marca quem a secretaria vinculou a um cadastro de membresia) fica de
// fora de proposito -- nenhum gate olha pra essa role em si, e sim pro
// vinculo Member em si (ver getSimulatedHasMemberRecord mais abaixo).
// "MEMBRO" e "VISITANTE" abaixo sao pseudo-papeis que simulam esses dois
// cenarios de vinculo (COM e SEM cadastro de membresia), nao papeis reais
// do banco -- corrigido em 17/10/2026 depois do Drigo apontar que tinham
// comportamento diferente (visitante nao ve /financeiro, membro ve) e eu
// ter assumido errado antes que eram redundantes.
// "Músico" NAO fica mais aqui (30/10/2026, pedido do Drigo: "poderia por
// Escala - Musico como os demais") -- foi pro grupo "Escalas por
// ministerio" abaixo, junto dos outros 9, ja que capacidade de musico e'
// so' mais uma flag de ministerio na Person (isMusician) desde 20/10/2026,
// sem motivo pra ficar destacado separado dos outros.
export const SIMULATABLE_ROLES = ["SECRETARIA", "SECRETARIA_CONSULTA", "ACOLHIMENTO", "RECEPCAO", "FINANCEIRO", "EDITOR_CONTEUDO", "MEMBRO", "VISITANTE"];
export const SIMULATABLE_ROLE_LABELS = { SECRETARIA: "Secretaria", SECRETARIA_CONSULTA: "Secretaria (consulta)", ACOLHIMENTO: "Acolhimento", RECEPCAO: "Recepção", FINANCEIRO: "Financeiro", EDITOR_CONTEUDO: "Editor de conteúdo", MEMBRO: "Membro (com cadastro de membresia)", VISITANTE: "Visitante (sem cadastro de membro)" };

// Simulador das 10 abas de Escala por ministerio (28/10/2026, pedido do
// Drigo depois da generalizacao de autoacesso -- ate entao so' dava pra
// simular Musico separado, fora deste grupo). Cada entrada e' um
// PSEUDO-papel (prefixo "ESCALA_", nunca existe no banco, igual
// MEMBRO/VISITANTE acima) que forca a flag correspondente da Person (ver
// ESCALA_TIPO_FLAG em lib/person.js, mosaico-api, e o mirror em
// AdminEscalas.jsx/PrototypeApp.js/InstitutionalLayout.jsx -- mesmo mapa
// tipo->flag, repetido aqui so' com o rotulo de exibicao) a dar true.
// MUSICOS entrou nesta lista em 30/10/2026 (antes vivia soltinho como
// pseudo-papel "MUSICO" la' em cima, unico fora do padrao -- getSimulatedIsMusician
// abaixo agora so' delega pra getSimulatedMinistryFlag, pra nenhum dos
// varios lugares que ja chamam getSimulatedIsMusician precisar mudar).
export const SIMULATABLE_MINISTRY_TIPOS = [
  { tipo: "MUSICOS", flag: "isMusician", label: "Escala · Músico" },
  { tipo: "ZELADORIA", flag: "isZelador", label: "Escala · Zeladoria" },
  { tipo: "KIDS", flag: "isKidsVoluntario", label: "Escala · Kids" },
  { tipo: "TEENS", flag: "isTeensVoluntario", label: "Escala · Teens" },
  { tipo: "MIDIA", flag: "isMidiaVoluntario", label: "Escala · Mídia" },
  { tipo: "CAFE", flag: "isCafeVoluntario", label: "Escala · Café" },
  { tipo: "RECEPCAO", flag: "isRecepcaoVoluntario", label: "Escala · Recepção" },
  { tipo: "PODCAST", flag: "isPodcastVoluntario", label: "Escala · Podcast" },
  { tipo: "PREGACAO", flag: "isPregacaoVoluntario", label: "Escala · Pregação" },
  { tipo: "TEENS_DEVOCIONAL", flag: "isTeensDevocionalVoluntario", label: "Escala · Devocional Teens" }
];

for (const { tipo, label } of SIMULATABLE_MINISTRY_TIPOS) {
  const role = `ESCALA_${tipo}`;
  SIMULATABLE_ROLES.push(role);
  SIMULATABLE_ROLE_LABELS[role] = label;
}

const MINISTRY_FLAG_BY_SIMULATED_ROLE = Object.fromEntries(
  SIMULATABLE_MINISTRY_TIPOS.map(({ tipo, flag }) => [`ESCALA_${tipo}`, flag])
);

export function getViewAsRole() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return SIMULATABLE_ROLES.includes(value) ? value : null;
  } catch {
    return null;
  }
}

// role = "SECRETARIA" | "ACOLHIMENTO" | null (null = voltar a ver como Admin de verdade)
export function setViewAsRole(role) {
  try {
    if (role && SIMULATABLE_ROLES.includes(role)) {
      window.localStorage.setItem(STORAGE_KEY, role);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage indisponivel (aba anonima, storage bloqueado, etc.) --
    // segue sem simular em vez de quebrar a pagina.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function clearViewAsRole() {
  setViewAsRole(null);
}

// Avisa quem estiver escutando (ex.: a faixa fixa "Vendo como") quando o
// papel simulado muda -- inclusive entre abas, via evento nativo "storage".
export function onViewAsRoleChange(handler) {
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

// So quem tem ADMIN nas roles reais consegue simular; pra qualquer outra
// pessoa (sem ADMIN) devolve as roles reais sem alteracao nenhuma. "MEMBRO"
// e "VISITANTE" sao pseudo-papeis: nao existem de verdade no banco como
// role (representam "sem nenhuma role especial", so diferem no vinculo de
// membresia -- ver getSimulatedHasMemberRecord) -- por isso os dois viram
// lista vazia em vez de ["MEMBRO"]/["VISITANTE"], que nenhum gate de role
// reconheceria mesmo.
export function applyViewAs(realRoles, viewAsRole) {
  const roles = Array.isArray(realRoles) ? realRoles : [];
  if (!roles.includes("ADMIN")) return roles;
  if (!viewAsRole) return roles;
  // MEMBRO/VISITANTE/ESCALA_* sao pseudo-papeis (nao existem no banco) --
  // nao haveria role real nenhuma pra devolver aqui, exatamente como
  // MEMBRO/VISITANTE ja funcionava antes dos pseudo-papeis de ministerio
  // existirem.
  if (viewAsRole === "MEMBRO" || viewAsRole === "VISITANTE" || viewAsRole.startsWith("ESCALA_")) return [];
  return [viewAsRole];
}

// me = resposta real de /auth/me (nunca o papel simulado, sempre a
// sessao de verdade). Use isto em qualquer gate de tela/menu que hoje le
// me.roles direto, pra passar a respeitar o modo "ver como".
export function getEffectiveRoles(me) {
  const realRoles = Array.isArray(me?.roles) ? me.roles : [];
  return applyViewAs(realRoles, getViewAsRole());
}

export function isSimulating(me) {
  const realRoles = Array.isArray(me?.roles) ? me.roles : [];
  return realRoles.includes("ADMIN") && Boolean(getViewAsRole());
}

// Gates como /financeiro nao olham pra uma role de banco -- olham se a
// conta tem um cadastro de Member vinculado (hasMemberRecord, calculado em
// cada tela a partir de viewerUser.linkedMember). Isso nao e' uma role,
// entao applyViewAs/getEffectiveRoles nao resolve sozinho: cada tela que
// usa hasMemberRecord precisa chamar esta funcao tambem.
// - Simulando "Membro (com cadastro de membresia)": forca true, mesmo que
//   o ADMIN de verdade logado nao tenha Member vinculado.
// - Simulando "Visitante (sem cadastro de membro)": forca false, mesmo que
//   o ADMIN de verdade logado TENHA Member vinculado -- esse era o buraco
//   antes desta correcao (17/10/2026): nao dava pra simular esse cenario,
//   so o oposto.
// - Sem simular nenhum dos dois (outro papel, ou nenhum): usa o vinculo
//   real da propria conta (realHasMemberRecord).
export function getSimulatedHasMemberRecord(isRealAdmin, realHasMemberRecord) {
  if (!isRealAdmin) return Boolean(realHasMemberRecord);
  const viewAsRole = getViewAsRole();
  if (viewAsRole === "MEMBRO") return true;
  if (viewAsRole === "VISITANTE") return false;
  return Boolean(realHasMemberRecord);
}

// Mesmo racional de getSimulatedHasMemberRecord, mas pra capacidade de
// musico -- que desde 20/10/2026 mora na Person (me.person?.isMusician),
// nao em me.roles (pedido do Rodrigo: "o papel n deveria estar atrela ao
// membro ou user, e sim a pessoa"). Como isMusician nao e' uma role, nao
// e' coberta por applyViewAs/getEffectiveRoles sozinha -- qualquer gate de
// tela/menu que ler me.person?.isMusician precisa chamar esta funcao
// tambem pra respeitar o modo "ver como":
// - Simulando "Músico": forca true, mesmo que o ADMIN de verdade nao
//   tenha a propria Person marcada como musico.
// - Simulando qualquer outro papel (inclusive MEMBRO/VISITANTE): forca
//   false, senao um ADMIN que tambem e' musico de verdade continuaria
//   vendo Escala ao simular outro papel, invalidando a simulacao -- mesmo
//   tipo de buraco que o comentario acima descreve pra VISITANTE.
// - Sem simular nada: usa o valor real da propria conta.
export function getSimulatedIsMusician(isRealAdmin, realIsMusician) {
  // So' delega pro mecanismo generico (mesma logica, Musico agora e' so'
  // mais uma entrada em SIMULATABLE_MINISTRY_TIPOS com flag "isMusician")
  // -- ver getSimulatedMinistryFlag mais abaixo. Mantido como funcao a
  // parte (em vez de apagar e trocar os varios lugares que ja chamam
  // getSimulatedIsMusician) pra nao precisar tocar em AdminHome.jsx,
  // AdminRepertorio.jsx, RepertorioMusico.jsx, PrototypeApp.js e
  // InstitutionalLayout.jsx so' por causa dessa mudanca de rotulo.
  return getSimulatedMinistryFlag(isRealAdmin, "isMusician", realIsMusician);
}

// Mesmo racional de getSimulatedIsMusician, generalizado pras outras 9
// flags de ministerio (ver SIMULATABLE_MINISTRY_TIPOS acima). `flag` e' o
// nome do campo na Person (ex.: "isZelador", "isKidsVoluntario") -- cada
// tela que ja le viewerUser.person[flag] direto pra decidir o que mostrar
// (canSeeEscala em PrototypeApp.js/InstitutionalLayout.jsx, ownTipos em
// AdminEscalas.jsx) deve trocar pra chamar esta funcao, igual ja fazia
// com getSimulatedIsMusician:
// - Simulando a propria aba (ex.: "Escala · Zeladoria" -> flag
//   isZelador): forca true mesmo que o ADMIN de verdade nao tenha essa
//   flag marcada.
// - Simulando qualquer outra coisa (outro papel, Musico, Membro,
//   Visitante, ou outra aba de ministerio): forca false, senao um ADMIN
//   que tambem for, por ex., de verdade Zeladoria continuaria vendo
//   aquela aba ao simular outro papel, invalidando a simulacao.
// - Sem simular nada: usa o valor real da propria conta.
export function getSimulatedMinistryFlag(isRealAdmin, flag, realValue) {
  if (!isRealAdmin) return Boolean(realValue);
  const viewAsRole = getViewAsRole();
  if (!viewAsRole) return Boolean(realValue);
  const simulatedFlag = MINISTRY_FLAG_BY_SIMULATED_ROLE[viewAsRole];
  if (simulatedFlag) return simulatedFlag === flag;
  return false;
}
